import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Crown, LogOut, Heart, Vote, Loader2, ShieldCheck, Gavel, FileText, Eye, TrendingUp, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CandidateCard from "@/components/CandidateCard";
import VerificationPanel from "@/components/VerificationPanel";
import { QRCodeSVG } from "qrcode.react";

const STATUS_STEPS = ["submitted", "under_review", "interview", "approved"];

export default function Account() {
  const { user, loading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [application, setApplication] = useState(null);
  const [myCandidate, setMyCandidate] = useState(null);
  const [tickets, setTickets] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (user) {
      api.get("/me/dashboard").then((r) => setData(r.data)).catch(() => {});
      api.get("/me/application").then((r) => setApplication(r.data)).catch(() => {});
      api.get("/me/tickets").then((r) => setTickets(r.data)).catch(() => {});
      if (user.candidate_id) api.get("/me/candidate").then((r) => setMyCandidate(r.data)).catch(() => {});
    }
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const isAdmin = user.role === "admin" || user.role === "super_admin";
  const isJudge = user.role === "judge";
  const isCandidate = user.role === "candidate";

  return (
    <div className="section py-12" data-testid="account-page">
      <div className="glass rounded-2xl p-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {user.picture ? <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover" /> : <span className="flex h-14 w-14 items-center justify-center rounded-full gold-bg text-obsidian"><Crown className="h-6 w-6" /></span>}
          <div>
            <h1 className="text-2xl text-amber-50">{user.name}</h1>
            <p className="text-sm text-amber-100/60">{user.email}</p>
            <span className="mt-1 inline-block rounded-full border border-gold/25 px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-gold">{user.role.replace("_", " ")}</span>
            {user.membership && user.membership !== "Free" && <span className="ml-2 mt-1 inline-block rounded-full gold-bg px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-obsidian" data-testid="membership-badge">{user.membership}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <Link to="/admin" className="btn-ghost-gold inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Command Center</Link>}
          {(isJudge || isAdmin) && <Link to="/judge" className="btn-ghost-gold inline-flex items-center gap-2"><Gavel className="h-4 w-4" /> Judges' Chamber</Link>}
          {(user.role === "national_director" || isAdmin) && <Link to="/director" className="btn-ghost-gold inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Director Portal</Link>}
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="rounded-full border border-gold/30 px-5 py-3 text-sm text-gold hover:bg-gold/10 inline-flex items-center gap-2"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
      </div>

      {/* Candidate dashboard */}
      {isCandidate && myCandidate && (
        <div className="mt-6" data-testid="candidate-dashboard">
          <p className="kicker mb-4">My Candidate Dashboard</p>
          <div className="grid gap-4 sm:grid-cols-4">
            {[
              { Icon: Vote, label: "My Votes", value: myCandidate.votes.toLocaleString() },
              { Icon: TrendingUp, label: "Continental Rank", value: `#${myCandidate.rank} / ${myCandidate.total_candidates}` },
              { Icon: Eye, label: "Profile Views", value: Number(myCandidate.page_views).toLocaleString() },
              { Icon: Sparkles, label: "Stage", value: myCandidate.stage },
            ].map((m) => (
              <div key={m.label} className="card-lux p-6"><m.Icon className="mb-2 h-5 w-5 text-gold" /><p className="font-mono text-2xl gold-text">{m.value}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">{m.label}</p></div>
            ))}
          </div>
          <Link to={`/candidates/${myCandidate.slug}`} className="btn-ghost-gold mt-4 inline-block">View my public profile</Link>
        </div>
      )}

      {/* Application tracker */}
      {!isCandidate && (
        <div className="mt-6" data-testid="application-tracker">
          <p className="kicker mb-4">Candidate Application</p>
          {application?.status && application.status !== "draft" ? (
            <div className="card-lux p-6">
              <p className="mb-4 text-sm text-amber-100/70">Your application status: <span className="gold-text font-semibold">{application.status.replace("_", " ").toUpperCase()}</span></p>
              <div className="flex flex-wrap gap-2">
                {STATUS_STEPS.map((s) => {
                  const reached = STATUS_STEPS.indexOf(application.status) >= STATUS_STEPS.indexOf(s) || application.status === "approved";
                  return <span key={s} className={`rounded-full px-3 py-1.5 text-xs ${reached ? "gold-bg text-obsidian" : "border border-gold/20 text-amber-50/40"}`}>{s.replace("_", " ")}</span>;
                })}
              </div>
              {application.status === "rejected" && <p className="mt-3 text-xs text-red-300">This application was not selected this edition.</p>}
              {application.status === "waitlisted" && <p className="mt-3 text-xs text-amber-300">You are on the waitlist.</p>}
              <Link to="/apply" className="btn-ghost-gold mt-4 inline-block">View application</Link>
            </div>
          ) : (
            <div className="card-lux flex flex-col items-start gap-3 p-6 sm:flex-row sm:items-center sm:justify-between">
              <div><h3 className="font-serif text-xl text-amber-50 flex items-center gap-2"><FileText className="h-5 w-5 text-gold" /> {application?.status === "draft" ? "Continue your application" : "Dream of the crown?"}</h3><p className="mt-1 text-sm text-amber-100/60">Apply to represent your delegation on the continental stage.</p></div>
              <Link to="/apply" className="btn-gold" data-testid="apply-cta">{application?.status === "draft" ? "Resume Application" : "Apply Now"}</Link>
            </div>
          )}
        </div>
      )}

      {/* Voter verification */}
      {!isCandidate && <VerificationPanel />}

      {/* My Tickets */}
      {tickets.length > 0 && (
        <div className="mt-6" data-testid="my-tickets">
          <p className="kicker mb-4">My Event Tickets</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {tickets.map((t) => (
              <div key={t.id} className={`card-lux flex items-center gap-4 p-4 ${t.used ? "opacity-60" : ""}`} data-testid={`ticket-${t.code}`}>
                <div className="rounded-lg bg-white p-2"><QRCodeSVG value={t.code} size={72} /></div>
                <div className="min-w-0">
                  <p className="truncate text-sm text-amber-50">{t.event_name}</p>
                  <p className="text-xs text-gold">{t.tier}</p>
                  <p className="mt-1 font-mono text-xs text-amber-100/60">{t.code}</p>
                  <p className={`text-[0.65rem] uppercase tracking-wider ${t.used ? "text-red-300" : "text-emerald-accent"}`}>{t.used ? "Checked in" : "Valid"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="card-lux p-6"><Vote className="mb-2 h-5 w-5 text-gold" /><p className="font-mono text-2xl gold-text">{data?.total_votes ?? 0}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Votes Cast</p></div>
        <div className="card-lux p-6"><Heart className="mb-2 h-5 w-5 text-gold" /><p className="font-mono text-2xl gold-text">{data?.favorites?.length ?? 0}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Favorites</p></div>
        <div className="card-lux p-6"><Sparkles className="mb-2 h-5 w-5 text-gold" /><p className="font-mono text-2xl gold-text">{user.vote_credits ?? 0}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Vote Credits</p></div>
      </div>

      <p className="kicker mb-5 mt-12">My Favorite Candidates</p>
      {data?.favorites?.length ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {data.favorites.map((c, i) => <CandidateCard key={c.id} candidate={c} index={i} />)}
        </div>
      ) : (
        <div className="card-lux p-10 text-center">
          <p className="text-amber-100/60">You haven't favorited anyone yet.</p>
          <Link to="/candidates" className="btn-ghost-gold mt-5 inline-block">Explore candidates</Link>
        </div>
      )}
    </div>
  );
}
